import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatMoney } from '@/features/sales';

import type { DepositSummary } from './deposit-badge';
import type { ForfeitAction } from './forfeit-decision';

export interface ForfeitConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which staff-marked outcome is about to be applied. */
  action: ForfeitAction;
  /** The deposit about to be kept. Only ever rendered for a `paid` one. */
  deposit: DepositSummary;
  /** Apply the status change. The dialog closes itself first. */
  onConfirm: () => void;
  /** Disables both buttons while the mutation is in flight. */
  busy?: boolean;
}

/**
 * The confirmation step in front of a STAFF-marked forfeit (§4.4).
 *
 * Before this existed, selecting "Cancelled" or "No-show" on the calendar took
 * a real, captured deposit off a real customer on a single click, with no
 * warning and no undo — the org's forfeit switches were applied server-side
 * and the person applying them was never told. The forfeit itself is
 * unchanged; this only makes it deliberate.
 *
 * It names the AMOUNT rather than saying "the deposit". "Keep €50.00 and mark
 * this a no-show?" is a decision a receptionist can actually make; "forfeit
 * the deposit?" is not.
 *
 * Deliberately NOT rendered when nothing would be forfeited — see
 * `wouldForfeitDeposit`. A dialog that appears on every cancellation, mostly
 * saying nothing will happen, is a dialog people learn to dismiss unread.
 */
export function ForfeitConfirmDialog({
  open,
  onOpenChange,
  action,
  deposit,
  onConfirm,
  busy,
}: ForfeitConfirmDialogProps) {
  const balances = [
    deposit.amountCents,
    deposit.refundedAmountCents ?? 0,
    deposit.refundPendingAmountCents ?? 0,
    deposit.forfeitedAmountCents ?? 0,
  ];
  const remaining =
    deposit.amountCents === null
      ? null
      : deposit.amountCents -
        (deposit.refundedAmountCents ?? 0) -
        (deposit.refundPendingAmountCents ?? 0) -
        (deposit.forfeitedAmountCents ?? 0);
  const unknown =
    deposit.reconciliationRequired ||
    balances.some(
      (value) => value === null || !Number.isSafeInteger(value) || value < 0
    ) ||
    remaining === null ||
    remaining < 0;
  const canConfirm =
    !unknown &&
    remaining !== null &&
    remaining > 0 &&
    deposit.status === 'paid';
  const amount =
    unknown || remaining === null
      ? null
      : formatMoney(remaining, deposit.currency);
  const isNoShow = action === 'no_show';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {unknown
              ? 'Reconcile this payment first'
              : !canConfirm
                ? 'No payment remains to keep'
                : isNoShow
                  ? `Keep the ${amount} deposit and mark this a no-show?`
                  : `Keep the ${amount} deposit and cancel this booking?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {unknown
              ? 'The amount available to keep is unknown. Reconcile this payment before changing the booking.'
              : !canConfirm
                ? 'No payment is available to forfeit.'
                : isNoShow
                  ? `The customer has paid a ${amount} deposit. Your no-show policy keeps it — they will not be refunded.`
                  : `The customer has paid a ${amount} deposit and this booking is inside your cancellation deadline, so your policy keeps it — they will not be refunded.`}{' '}
            {canConfirm ? 'You can change this in Settings → Bookings.' : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Keep booking</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              if (!canConfirm || busy) {
                event.preventDefault();
                return;
              }
              onOpenChange(false);
              onConfirm();
            }}
            disabled={busy || !canConfirm}
          >
            {unknown
              ? 'Payment needs reconciliation'
              : !canConfirm
                ? 'Nothing to keep'
                : isNoShow
                  ? `Mark no-show, keep ${amount}`
                  : `Cancel, keep ${amount}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
